export type LogLevel = "debug" | "info" | "warn" | "error";

export interface ChatClientConfig {
  baseUrl: string;
  siteId: string;
  timeoutMs?: number;
  logger?: (level: LogLevel, ...args: unknown[]) => void;
}

export interface StepInfo {
  type: string;
  status: 'started' | 'done';
  label: string;
  index?: number;
}

export interface SendMessageParams {
  chatId: string;
  message: string;
  onChunk?: (chunk: string, fullText: string) => void;
  onDone?: (result: SendMessageResult) => void;
  onStep?: (step: StepInfo) => void;
  signal?: AbortSignal;
}

export interface Citation {
  title: string;
  uri: string;
  text: string;
  customMetadata: Record<string, string | number>;
}

export interface SendMessageResult {
  fullText: string;
  citations: Citation[];
}

export type ClientEvent =
  | { type: "rate_limit"; chatId?: string; detail?: string }
  | { type: "error"; error: unknown; chatId?: string }
  | { type: "proactive"; chatId: string; text: string };

export class ChatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChatError";
  }
}

export class ChatAuthError extends ChatError {
  constructor(message = "Authentication failed") {
    super(message);
    this.name = "ChatAuthError";
  }
}

export class ChatValidationError extends ChatError {
  constructor(message = "Invalid input") {
    super(message);
    this.name = "ChatValidationError";
  }
}

export class ChatInProgressError extends ChatError {
  constructor(message = "Previous message still in progress for this chat") {
    super(message);
    this.name = "ChatInProgressError";
  }
}

export class RateLimitedError extends ChatError {
  constructor(message = "Rate limit exceeded") {
    super(message);
    this.name = "RateLimitedError";
  }
}

export interface SessionResponse {
  token: string;
  expires_in: number;
  session_id: string;
}
